import { AdminGameSelector } from "@/components/classic-admin/AdminGameSelector";
import { adminGameFromLocation } from "@/lib/admin-game-api";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Users, UserSearch, LogOut, X, History, UserCog, Swords, Newspaper, Flag, Database, Ticket, CalendarDays, AppWindow, ShieldCheck, Gift, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth-store";
import { logout } from "@/lib/api";
import { useDirtyStore } from "@/lib/dirty-store";
import { useOpenedPlayersStore } from "@/lib/opened-players-store";
import { playerSearchPath } from "@/lib/player-navigation";
import { playerSearchOrigin } from "@/lib/player-navigation";

const playerLinks = [
  { label: "Players", icon: Users, href: "/accounts" },
  { label: "PlayFab (legacy)", icon: UserSearch, href: "/players" },
];
const configurationLinks = [
  { label: "Game Versions", icon: AppWindow, href: "/game-versions" },
  { label: "Balance", icon: Database, href: "/balance" },
  { label: "Chamber of Trial", icon: Swords, href: "/trial" },
];
const liveLinks = [
  { label: "Purchases", icon: Database, href: "/purchases" },
  { label: "Coupons", icon: Ticket, href: "/coupons" },
  { label: "Events", icon: CalendarDays, href: "/events" },
  { label: "Premium Pass Raffle", icon: Gift, href: "/raffle" },
  { label: "News", icon: Newspaper, href: "/news" },
];
const administrationLinks = [
  { label: "Flags", icon: Flag, href: "/flags" },
  { label: "Account Recovery", icon: RotateCcw, href: "/account-recovery" },
  { label: "Dev Accounts", icon: ShieldCheck, href: "/dev-accounts" },
  { label: "Audit Logs", icon: History, href: "/audit" },
  { label: "Admins", icon: UserCog, href: "/admins" },
];

const navItemBase =
  "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors border border-transparent";
const navItemActive =
  "bg-accent text-accent-foreground font-medium border-border/60 shadow-sm";
const navItemInactive =
  "text-muted-foreground hover:bg-accent/70 hover:text-foreground";

export function NavSidebar() {
  const me = useAuthStore((s) => s.me);
  const clearAuth = useAuthStore((s) => s.clear);
  const confirmLeave = useDirtyStore((s) => s.confirmLeave);
  const opened = useOpenedPlayersStore((s) => s.list);
  const removeOpened = useOpenedPlayersStore((s) => s.remove);
  const location = useLocation();
  const game = adminGameFromLocation(location.pathname, location.search);
  const detailOrigin = playerSearchOrigin(location.state);
  const onDetail = /^\/players\/classic\/[^/]+$/.test(location.pathname);
  const nav = useNavigate();

  // Intercept any navigation that would leave the current page while dirty.
  function guardedClick(e: React.MouseEvent) {
    if (!confirmLeave()) e.preventDefault();
  }

  async function handleLogout() {
    if (!confirmLeave()) return;
    await logout().catch(() => {});
    clearAuth();
    window.location.href = "/login";
  }

  function handleRemoveOpened(e: React.MouseEvent, playfabId: string) {
    e.preventDefault();
    e.stopPropagation();
    // If the user is removing the player they're currently viewing, that's
    // also a navigation away — guard it.
    const currentlyViewing = location.pathname === `/players/classic/${playfabId}`;
    if (currentlyViewing && !confirmLeave()) return;
    removeOpened(playfabId);
    if (currentlyViewing) nav(playerSearchPath(opened.find((p) => p.playfabId === playfabId)?.origin ?? "accounts"));
  }

  function renderLinks(links: typeof playerLinks) {
    return links.map(({ label, icon: Icon, href }) => (
      <NavLink key={href} to={href} end onClick={guardedClick}
        className={({ isActive }) => cn(navItemBase,
          isActive || (href === '/games/ec1/players' && location.pathname.startsWith('/games/ec1/players/')) || (href === '/account-recovery' && location.pathname.startsWith('/account-recovery/')) || (onDetail && href === playerSearchPath(detailOrigin)) ? navItemActive : navItemInactive)}>
        <Icon className="h-4 w-4 shrink-0 opacity-90" />
        <span className="truncate">{label}</span>
      </NavLink>
    ));
  }

  return (
    <aside className="w-56 shrink-0 border-r border-border bg-shell-sidebar flex flex-col">
      <div className="px-4 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.45)]" />
          <AdminGameSelector className="flex-1" />
        </div>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-3 overflow-y-auto">
        {game === 'ec1' ? <div className="space-y-0.5">
          <h2 className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Epic Conquest Classic</h2>
          {renderLinks([
            { label: 'Players', icon: Users, href: '/games/ec1/players' },
            { label: 'PlayFab (legacy)', icon: UserSearch, href: '/games/ec1/legacy' },
            { label: 'Audit Logs', icon: History, href: '/games/ec1/audit' },
            { label: 'Service Status', icon: AppWindow, href: '/games/ec1/status' },
            { label: 'Admins', icon: UserCog, href: '/admins?game=ec1' },
          ])}
        </div> : <>
        <div className="space-y-0.5">
          <h2 className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Player support</h2>
          {renderLinks(playerLinks)}
        {/* Opened accounts stay below both search entries. */}
        {opened.length > 0 && (
          <div className="pl-3 mt-0.5 mb-1 border-l border-border/60 ml-3 space-y-0.5">
            {opened.map((p) => {
              const active = location.pathname === `/players/classic/${p.playfabId}`;
              return (
                <NavLink
                  key={p.playfabId}
                  to={`/players/classic/${p.playfabId}`}
                  state={{ origin: p.origin }}
                  onClick={guardedClick}
                  className={cn(
                    "group flex items-center justify-between gap-1 pl-2 pr-1 py-1.5 rounded-md text-xs transition-colors",
                    active
                      ? "bg-blue-600/30 text-blue-100 font-medium ring-1 ring-blue-500/50"
                      : "text-muted-foreground hover:bg-accent/40 hover:text-foreground"
                  )}
                  title={p.playfabId}
                >
                  <span className="truncate">{p.label}</span>
                  <button
                    type="button"
                    onClick={(e) => handleRemoveOpened(e, p.playfabId)}
                    className="shrink-0 p-0.5 rounded opacity-50 hover:opacity-100 hover:bg-destructive/30 hover:text-destructive-foreground transition"
                    aria-label={`Close ${p.label}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </NavLink>
              );
            })}
          </div>
        )}
        </div>
        <div className="space-y-0.5">
          <h2 className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Game configuration</h2>
          {renderLinks(configurationLinks)}
        </div>
        <div className="space-y-0.5">
          <h2 className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Live operations</h2>
          {renderLinks(liveLinks.filter((l) => l.href !== "/purchases" || me?.role === "internal"))}
        </div>
        <div className="space-y-0.5">
          <h2 className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Administration</h2>
          {renderLinks(administrationLinks)}
        </div>
        </>}
      </nav>

      {me && (
        <div className="border-t border-border p-3 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            {me.avatar && (
              <img
                src={`https://cdn.discordapp.com/avatars/${me.discordId}/${me.avatar}.png?size=32`}
                alt=""
                className="h-6 w-6 rounded-full"
              />
            )}
            <span className="truncate text-foreground/90">{me.globalName ?? me.username}</span>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      )}
    </aside>
  );
}
